/**
 * JWT Utilities - Helper functions for decoding and parsing JWT tokens
 *
 * Security Note: This is CLIENT-SIDE ONLY for reading claims.
 * Backend MUST validate all tokens - these are for UX convenience only.
 */

/**
 * Decoded JWT payload structure
 */
export interface JwtPayload {
  userId?: string;
  email?: string;
  userName?: string;
  businessId?: string; // Present for BusinessOwner users
  role?: string;
  roles?: string[];
  nbf?: number; // Not Before timestamp
  exp?: number; // Expiration timestamp
  iat?: number; // Issued At timestamp
  iss?: string; // Issuer
  aud?: string; // Audience
  [key: string]: unknown; // Allow additional claims
}

/**
 * Decode a JWT token and return the payload
 *
 * Security: This does NOT validate the token signature!
 * Backend must validate all tokens. This is only for reading claims.
 *
 * @param token - The JWT token string
 * @returns Decoded payload object
 * @throws Error if token format is invalid
 */
export function decodeJwt(token: string): JwtPayload {
  if (!token || typeof token !== 'string') {
    throw new Error('Invalid token: must be a non-empty string');
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    throw new Error('Invalid JWT format: must have 3 parts separated by dots');
  }

  try {
    // JWT payload is the second part, base64url encoded
    const payload = parts[1];
    if (!payload) {
      throw new Error('Invalid JWT: missing payload');
    }

    // Decode base64url to string
    const decodedString = base64UrlDecode(payload);

    // Parse JSON
    const parsedPayload = JSON.parse(decodedString) as JwtPayload;

    return parsedPayload;
  } catch (error) {
    if (error instanceof Error) {
      throw new Error(`Failed to decode JWT: ${error.message}`);
    }
    throw new Error('Failed to decode JWT: unknown error');
  }
}

/**
 * Decode a base64url encoded string
 *
 * Base64url is base64 with URL-safe characters:
 * - No padding (=)
 * - - instead of +
 * - _ instead of /
 */
function base64UrlDecode(base64Url: string): string {
  // Convert base64url to base64
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  const padding = base64.length % 4;
  if (padding > 0) {
    base64 += '='.repeat(4 - padding);
  }

  // Decode base64 to string
  try {
    // Browser-compatible base64 decode
    const decoded = atob(base64);

    // Handle UTF-8 encoding
    return decodeURIComponent(
      decoded
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
  } catch (error) {
    throw new Error('Failed to decode base64url string');
  }
}

/**
 * Extract a specific claim from a JWT token
 *
 * @param token - The JWT token string
 * @param claimName - Name of the claim to extract
 * @returns Claim value or null if not present
 */
export function extractClaim(token: string, claimName: string): string | null {
  try {
    const payload = decodeJwt(token);
    const value = payload[claimName];

    if (value === undefined || value === null) {
      return null;
    }

    return String(value);
  } catch {
    return null;
  }
}

/**
 * Extract businessId claim from JWT token
 *
 * @param token - The JWT token string
 * @returns businessId or null if not present
 */
export function extractBusinessId(token: string): string | null {
  return extractClaim(token, 'businessId');
}

/**
 * Extract roles from JWT token
 * Handles both single role string and role array
 *
 * @param token - The JWT token string
 * @returns Array of role names
 */
export function extractRoles(token: string): string[] {
  try {
    const payload = decodeJwt(token);

    // Check for 'role' claim (single role as string)
    if (typeof payload['role'] === 'string') {
      return [payload['role']];
    }

    // Check for 'roles' claim (array of roles)
    if (Array.isArray(payload['roles'])) {
      return payload['roles'].filter((r): r is string => typeof r === 'string');
    }

    // Check for role claim in Microsoft Identity format
    // Microsoft Identity may use 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role'
    const msRoleClaim = 'http://schemas.microsoft.com/ws/2008/06/identity/claims/role';
    if (payload[msRoleClaim]) {
      const roleValue = payload[msRoleClaim];
      if (typeof roleValue === 'string') {
        return [roleValue];
      }
      if (Array.isArray(roleValue)) {
        return roleValue.filter((r): r is string => typeof r === 'string');
      }
    }

    return [];
  } catch {
    return [];
  }
}

/**
 * Check if JWT token is expired
 *
 * @param token - The JWT token string
 * @returns true if token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  try {
    const payload = decodeJwt(token);

    if (!payload.exp) {
      // No expiration claim - consider it expired for safety
      return true;
    }

    // exp is in seconds since epoch, Date.now() is in milliseconds
    const expirationTime = payload.exp * 1000;
    return Date.now() >= expirationTime;
  } catch {
    // Failed to decode - consider expired
    return true;
  }
}

/**
 * Get token expiration time as ISO string
 *
 * @param token - The JWT token string
 * @returns ISO datetime string or null if no expiration
 */
export function getTokenExpiration(token: string): string | null {
  try {
    const payload = decodeJwt(token);

    if (!payload.exp) {
      return null;
    }

    // exp is in seconds since epoch
    const expirationDate = new Date(payload.exp * 1000);
    return expirationDate.toISOString();
  } catch {
    return null;
  }
}
